// Session 33, rebuilt in Session 34: the sign-up page.
//
// Public, no session. Reads the catalogue with the anon key and writes one row
// through portal_request_signup, which validates everything it is handed
// because the caller is a stranger.
//
// Leena, after seeing the first version: group the services, say what each one
// actually is, keep the summary short with a More to open, and show the
// benefit. A service sold at two volumes is one block with two options inside;
// AI courses is one block with a row per course, because somebody may want one
// course and not another. Both shapes come from the catalogue
// (lotus_plan_groups.choice), not from anything hardcoded here.
//
// The trial is 14 days and it is not extended. An earlier version promised to
// push it out if their answers had not come back and promised never to charge
// before they had seen something; she took both out. "That's really up to
// them."
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase.js';

// The button once Stripe is connected. Leena asked for "Pay"; this says "Start
// my free trial" because the page's own headline offers 14 free days and a
// button marked Pay beside it contradicts itself. One string to change if she
// would rather have her word.
const PAY_LABEL = 'Start my free trial';

const money = (cents) => {
  const d = cents / 100;
  return '$' + (Number.isInteger(d) ? d.toFixed(0) : d.toFixed(2));
};

export default function Signup() {
  const [groups, setGroups] = useState(null);
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState([]);
  const [billing, setBilling] = useState('monthly');
  const [openGroup, setOpenGroup] = useState({});
  const [form, setForm] = useState({ email: '', business_name: '', website: '', notes: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [banner, setBanner] = useState(null);
  // Stripe sends people back to /signup?paid=1 or ?cancelled=1. Read it once
  // on mount: the URL is the only thing that survives the redirect.
  const [returned] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('paid') === '1') return 'paid';
    if (q.get('cancelled') === '1') return 'cancelled';
    return null;
  });

  useEffect(() => {
    (async () => {
      const [g, p] = await Promise.all([
        supabase.from('v_portal_plan_groups').select('*').order('sort'),
        supabase.from('v_portal_plans').select('*').order('sort'),
      ]);
      if (g.error || p.error) { setError((g.error || p.error).message); return; }
      setGroups(g.data || []);
      setPlans(p.data || []);
    })();
  }, []);

  const plansOf = (code) => (plans || []).filter((p) => p.group_code === code);
  const byCode = (code) => (plans || []).find((p) => p.plan_code === code);
  // Session 47: a plan can come with another one (the newsletter with the blog
  // plan of the same volume, lotus_plans.requires_plan_code). The option is
  // greyed out until that plan is in the basket, follows a switch of volume, and
  // goes when its plan goes. portal_request_signup enforces the same rule.
  const needOf = (p) => (p.requires_plan_code ? byCode(p.requires_plan_code) : null);
  const needMet = (p) => !p.requires_plan_code || picked.includes(p.requires_plan_code);

  const chosen = useMemo(
    () => (plans || []).filter((p) => picked.includes(p.plan_code)),
    [plans, picked],
  );

  const total = useMemo(() => chosen.reduce(
    (t, p) => t + (billing === 'annual' ? p.price_annual_cents : p.price_monthly_cents), 0,
  ), [chosen, billing]);

  // Twelve months of the monthly price, less the actual annual price. Read from
  // both columns rather than assuming annual is ten months, because the two can
  // be set independently and a saving we quote has to be the real one.
  const annualSaving = useMemo(() => chosen.reduce(
    (t, p) => t + p.price_monthly_cents * 12 - p.price_annual_cents, 0,
  ), [chosen]);

  const trialDays = useMemo(
    () => (chosen.length ? Math.max(...chosen.map((p) => p.trial_days || 14)) : 14),
    [chosen],
  );

  // Every chosen plan has to have its Stripe prices before a card can be taken.
  const canPay = chosen.length > 0 && chosen.every((p) => p.checkout_ready);

  // Dropping a plan also drops anything that came with it.
  const without = (cur, codes) => cur.filter((c) => {
    if (codes.includes(c)) return false;
    const p = byCode(c);
    return !(p && p.requires_plan_code && codes.includes(p.requires_plan_code));
  });

  function toggle(plan, group) {
    setPicked((cur) => {
      if (cur.includes(plan.plan_code)) return without(cur, [plan.plan_code]);
      // In a 'one' group the options are volumes of the same service, so
      // choosing one lets the others go. portal_request_signup enforces this
      // too: the page is not the boundary.
      const siblings = group.choice === 'one'
        ? plansOf(group.group_code).map((p) => p.plan_code).filter((c) => c !== plan.plan_code)
        : [];
      const dropped = cur.filter((c) => siblings.includes(c));
      let next = without(cur, dropped);
      // Session 47: an option that came with a dropped volume follows the
      // switch to the option that comes with the new one, if there is one.
      for (const c of cur) {
        const p = byCode(c);
        if (!p || !p.requires_plan_code || !dropped.includes(p.requires_plan_code)) continue;
        const swap = plansOf(p.group_code).find((q) => q.requires_plan_code === plan.plan_code);
        if (swap && !next.includes(swap.plan_code)) next = [...next, swap.plan_code];
      }
      return [...next, plan.plan_code];
    });
  }

  async function onSend(e) {
    e.preventDefault();
    if (!picked.length) { setBanner({ tone: 'err', text: 'Choose at least one service.' }); return; }
    setSending(true); setBanner(null);

    // The lead is written first, and it is written whatever happens next. An
    // abandoned checkout still leaves Leena somebody to email, with the basket
    // they chose, which a Stripe-first flow would lose.
    const { data: requestId, error: rpcErr } = await supabase.rpc('portal_request_signup', {
      p: {
        email: form.email,
        business_name: form.business_name,
        website: form.website,
        notes: form.notes,
        basket: picked,
        bill_interval: billing,
      },
    });
    if (rpcErr) { setSending(false); setBanner({ tone: 'err', text: rpcErr.message }); return; }

    if (!canPay) { setSending(false); setSent(true); return; }

    // Only the id goes to Stripe. The function reads the basket, the prices and
    // the trial length from the catalogue, so nothing here can name an amount.
    const { data, error: fnErr } = await supabase.functions.invoke('stripe-checkout', {
      body: { signup_request_id: requestId },
    });
    if (fnErr || !data?.url) {
      setSending(false);
      // The request is already saved, so this is a worse outcome rather than a
      // lost one, and the message has to say so or they will fill it in again.
      setBanner({
        tone: 'err',
        text: 'We saved your details but could not open the payment page. '
            + 'You do not need to do this again — we will email you to finish setting up.',
      });
      return;
    }
    window.location.assign(data.url);
  }

  if (error) {
    return (
      <div className="signup-page">
        <div className="empty">
          <h2>We couldn't load the price list</h2>
          <p>Please email leena@digitallotusmarketing.com and we'll sort it out by hand.</p>
        </div>
      </div>
    );
  }
  if (!groups || !plans) return <div className="signup-page"><p className="lede">Loading…</p></div>;

  if (returned === 'paid') {
    return (
      <div className="signup-page">
        <div className="signup-done">
          <h1>You're in — thank you</h1>
          <p className="lede">
            Your free trial has started. We'll email you within a day to get the
            details we need, and your first piece of work follows from there.
          </p>
          <p className="hint">
            Stripe has emailed you a receipt. Nothing is charged until your
            14 days are up, and you can stop before then without paying.
          </p>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="signup-page">
        <div className="signup-done">
          <h1>Thank you — that's with us</h1>
          <p className="lede">
            We'll email {form.email || 'you'} shortly to get you started. Nothing has
            been charged, and we'll confirm everything with you first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-trial-bar">
        <strong>14-day free trial.</strong> Start your free trial now.
      </div>

      {/* Backing out of Stripe is an ordinary thing to do, and their details
          are already saved, so this says so instead of looking like an error. */}
      {returned === 'cancelled' && (
        <div className="banner banner-info">
          No payment was taken. We still have your details, so you can pick up
          where you left off or just wait for our email.
        </div>
      )}

      <header className="signup-head">
        <h1>Digital Lotus</h1>
        <p className="lede">Pick the items you want to see the cost for.</p>
      </header>

      <div className="signup-interval" role="group" aria-label="How often you pay">
        <button type="button"
                className={`btn ${billing === 'monthly' ? 'btn-primary' : ''}`}
                aria-pressed={billing === 'monthly'}
                onClick={() => setBilling('monthly')}>
          Monthly
        </button>
        <button type="button"
                className={`btn ${billing === 'annual' ? 'btn-primary' : ''}`}
                aria-pressed={billing === 'annual'}
                onClick={() => setBilling('annual')}>
          Yearly <span className="signup-save-pill">2 months free</span>
        </button>
      </div>

      {groups.map((g) => {
        const opts = plansOf(g.group_code);
        const open = !!openGroup[g.group_code];
        return (
          <section key={g.group_code}
                   className={`signup-group${g.sellable ? '' : ' signup-group-soon'}`}>
            <div className="signup-group-main">
              <h2 className="signup-group-name">
                {g.name}
                {!g.sellable && <span className="chip chip-run-wait">not open yet</span>}
              </h2>
              <p className="signup-group-summary">
                {g.sellable ? g.summary : (g.unavailable_note || g.summary)}
              </p>
              {g.benefit && (
                <p className="signup-group-benefit">
                  <span aria-hidden="true">→ </span>{g.benefit}
                </p>
              )}
              {(g.details || []).length > 0 && (
                <>
                  <button type="button" className="link-btn signup-more"
                          aria-expanded={open}
                          onClick={() => setOpenGroup((o) => ({ ...o, [g.group_code]: !open }))}>
                    {open ? 'Less' : 'More — what you actually get'}
                  </button>
                  {open && (
                    <ul className="signup-group-details">
                      {(g.details || []).map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  )}
                </>
              )}
            </div>

            <div className="signup-options">
              {g.choice === 'one' && opts.length > 1 && (
                <p className="hint signup-pick-note">Choose one.</p>
              )}
              {opts.map((p) => {
                const on = picked.includes(p.plan_code);
                const price = billing === 'annual' ? p.price_annual_cents : p.price_monthly_cents;
                const need = needOf(p);
                const buyable = g.sellable && p.sellable && needMet(p);
                return (
                  <label key={p.plan_code}
                         className={`signup-option${on ? ' signup-option-on' : ''}`
                                    + (buyable ? '' : ' signup-option-off')}>
                    <input type="checkbox" checked={on} disabled={!buyable}
                           onChange={() => toggle(p, g)} />
                    <span className="signup-option-body">
                      <span className="signup-option-name">{p.name}</span>
                      <span className="signup-option-blurb">{p.blurb}</span>
                      {need && (
                        <span className="signup-option-needs">
                          {needMet(p) ? `Comes with ${need.name}.` : `Add ${need.name} first.`}
                        </span>
                      )}
                    </span>
                    <span className="signup-option-price">
                      {money(price)}
                      <span className="signup-option-per">
                        {billing === 'annual' ? '/year' : '/month'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}

      <div className="signup-total">
        <div>
          <strong>
            {picked.length === 0
              ? 'Nothing chosen yet'
              : `${money(total)} ${billing === 'annual' ? 'a year' : 'a month'}`}
          </strong>
          {picked.length > 0 && billing === 'annual' && annualSaving > 0 && (
            <span className="signup-total-note">
              {' '}— {money(annualSaving)} less than paying monthly
            </span>
          )}
        </div>
        {picked.length > 0 && (
          <p className="hint signup-trial">
            Your first {trialDays} days are free. We take your card to start and
            charge nothing until the {trialDays} days are up, and you can cancel
            before then without paying anything.
          </p>
        )}
      </div>

      <form className="signup-form" onSubmit={onSend}>
        <h2>Where should we reach you?</h2>
        <label className="field">
          <span className="field-label">Your email</span>
          <input type="email" required value={form.email} maxLength={200}
                 autoComplete="email"
                 onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Business name</span>
          <input type="text" value={form.business_name} maxLength={200}
                 autoComplete="organization"
                 onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Website</span>
          <input type="text" value={form.website} maxLength={400}
                 placeholder="yourbusiness.com"
                 onChange={(e) => setForm({ ...form, website: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Anything we should know?</span>
          <textarea rows={3} value={form.notes} maxLength={4000}
                    placeholder="What you do, who you want to reach, anything you'd rather we didn't write about."
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </label>

        {banner && (
          <div className={`banner banner-${banner.tone === 'ok' ? 'ok' : 'err'}`}>{banner.text}</div>
        )}

        <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="submit" className="btn btn-primary" disabled={sending || !picked.length}>
            {sending ? 'Sending…' : (canPay ? PAY_LABEL : 'Send this to Digital Lotus')}
          </button>
        </div>
        {/* Until every chosen plan has its Stripe prices, this page cannot take
            a card, and must not pretend to. The label above follows the same
            flag, so the day the keys are set it becomes a payment button with
            no change here. */}
        <p className="hint">
          {canPay
            ? 'Your card is taken securely by Stripe. We never see or store the number.'
            : 'Card payments are being switched on. Send this through and we will set you up by hand in the meantime — nothing is charged either way.'}
        </p>
      </form>

      <section className="signup-terms">
        <h2>Cancelling</h2>
        <ul>
          <li>
            Cancel any time before your renewal date and you won't be charged again.
          </li>
          <li>
            You keep everything you're subscribed to until the end of the period
            you've already paid for.
          </li>
          <li>
            We don't refund a payment that has already gone through. If you're
            charged and then decide to stop, get in touch and we'll look at it,
            but it is at our discretion.
          </li>
          <li>
            Six-monthly and yearly plans work the same way: tell us before the
            renewal date and that's that. If you'd rather move to month-to-month
            instead, just ask and we'll switch you over.
          </li>
        </ul>
      </section>
    </div>
  );
}
